/**
 * Replays (Phase 40.4; R11 step 7). A match is deterministic from its options (seed, roster, loadouts,
 * settings) plus the humans' inputs: bots recompute their own turns, and every human action enters the
 * Match as an input (Match.apply) and lands in its input log. So a replay record is the options, the
 * human deploy picks, and the human seats' part of the Match's input log — a few kilobytes — and playing
 * it back is running the game with every human team treated as a remote peer whose actions come from the
 * record instead of the network. The director paces it like any other match.
 *
 * The turn-clock host replays the log's turn inputs through its network path (a NetAction per input);
 * the action clock replays a log directly (tests/replayFromLog.test.ts). Toggles and drone inputs a human
 * sends on the turn clock are in the log but not yet replayed by this driver.
 *
 * Recording runs silently for every match and never changes how a match plays; watching a replay is a
 * separate action (main menu / game-over screen).
 */
import type { LoggedInput, MenuOptions, TurnInput, Vec3 } from '../types';
import type { NetAction, NetDeploySelection } from '../network/protocol';
import type { TurnSync } from '../network/sync';
import { BATTLE_VERSION } from '../match/match';
import { seatTeam } from '../match/input';

export interface ReplayDeploySelection { teamIndex: number; planetIndex: number; surfaceDir: Vec3 }
/** One logged input; `playerIndex` only on entries upgraded from a version-1 record (which stored it). */
export type ReplayInput = LoggedInput & { playerIndex?: number };
export interface ReplayRecord {
  version: 2;
  savedAt: string;
  /** The exact options the match started with (post-roster, post-shop). */
  options: MenuOptions;
  /** Whether the WASM kernel drove bots/physics; bots can diverge if this differs on playback. */
  wasm: boolean;
  /**
   * The rules version it was recorded under (Match.BATTLE_VERSION). A replay from another
   * version may play out differently — the moves are the same, the rules are not. Absent in
   * replays saved before versioning.
   */
  battleVersion?: number;
  deploySelections: ReplayDeploySelection[];
  /** The human seats' inputs, as the Match logged them (refusals included: the Match refuses them again). */
  inputs: ReplayInput[];
  result?: { winner: string | null; turns: number };
}

/** A human seat's entry (bots' actors are their unit ids; seats are negative). */
export const isHumanInput = (e: LoggedInput): boolean => seatTeam(e.actor) !== null;

export class ReplayRecorder {
  private deploys: ReplayDeploySelection[] = [];
  constructor(private readonly options: MenuOptions, private readonly wasm: boolean) {}
  deploy(teamIndex: number, planetIndex: number, surfaceDir: Vec3): void {
    this.deploys.push({ teamIndex, planetIndex, surfaceDir: { ...surfaceDir } });
  }
  /** Close the record over the Match's input log: the human seats' entries, in order. */
  finish(log: readonly LoggedInput[], result?: ReplayRecord['result']): ReplayRecord {
    return { version: 2, savedAt: new Date().toISOString(), options: this.options, wasm: this.wasm, battleVersion: BATTLE_VERSION, deploySelections: [...this.deploys], inputs: log.filter(isHumanInput).map(e => ({ ...e })), result };
  }
}

/**
 * Stands in for the network during playback: every human is "remote", their
 * actions and deploy picks come from the record, nothing is sent anywhere.
 */
export class ReplayDriver implements TurnSync {
  readonly kind = 'replay' as const;
  localTeamIndex: number;
  remoteTeamIndex = -1;
  remoteWasm: boolean | null;
  onActionQueued: (() => void) | null = null;
  onRemoteDeploySelection: ((msg: NetDeploySelection) => void) | null = null;
  onDesync: ((turnNumber: number, playerIndex: number, localHash: number, remoteHash: number) => void) | null = null;
  private cursor = 0;
  private deploysDrained = false;
  /** The turn inputs the turn-clock host can replay (its network path carries TurnInputs). */
  private readonly turns: Array<ReplayInput & { body: { kind: 'turn'; input: TurnInput } }>;
  /**
   * `playerIndexOf` resolves a unit id to its index in the running match's players (the host's
   * network path addresses players by index); version-1 records carry the index themselves.
   */
  constructor(readonly record: ReplayRecord, private readonly playerIndexOf: (playerId: number) => number = () => -1) {
    // The viewer watches from the first human team (fog cam follows them)
    const humans = record.options.humanTeams ?? 1;
    this.localTeamIndex = humans > 0 ? 0 : -1;
    this.remoteWasm = record.wasm;
    this.turns = record.inputs.filter((e): e is ReplayInput & { body: { kind: 'turn'; input: TurnInput } } => e.body.kind === 'turn');
  }
  get remaining(): number { return this.turns.length - this.cursor; }
  setTeamIndices(): void { /* fixed by the record */ }
  isRemoteTeam(_teamIndex: number): boolean { return true; }
  isRemoteHuman(player: { teamIndex: number; isBot: boolean }): boolean { return !player.isBot; }
  isLocalHuman(_player: { teamIndex: number; isBot: boolean }): boolean { return false; }
  sendHello(): void {}
  sendAction(): void {}
  sendDeploySelection(): void {}
  sendChecksum(): void {}
  peekAction(): NetAction | null {
    const e = this.turns[this.cursor];
    if (!e) return null;
    return { type: 'action', seq: this.cursor + 1, turnNumber: e.turn, playerIndex: e.playerIndex ?? this.playerIndexOf(e.body.input.playerId), input: e.body.input };
  }
  takeAction(): NetAction | null {
    const a = this.peekAction();
    if (a) this.cursor++;
    return a;
  }
  drainDeploySelections(): NetDeploySelection[] {
    if (this.deploysDrained) return [];
    this.deploysDrained = true;
    return this.record.deploySelections.map(d => ({ type: 'deploySelection' as const, teamIndex: d.teamIndex, planetIndex: d.planetIndex, surfaceDir: d.surfaceDir }));
  }
}

export const REPLAY_STORAGE_KEY = 'orbituary-replay-last';

/** Why a replay may not play back as recorded (the rules changed since), or null. */
export function replayVersionWarning(record: Pick<ReplayRecord, 'battleVersion'>): string | null {
  if (record.battleVersion === BATTLE_VERSION) return null;
  const from = record.battleVersion === undefined ? 'an earlier version of the rules' : `rules version ${record.battleVersion}`;
  return `Replay recorded under ${from} (now ${BATTLE_VERSION}) — it may play out differently.`;
}

export function serializeReplay(record: ReplayRecord): string {
  return JSON.stringify(record);
}

/** A version-1 record's action entry (kept so old files still load). */
interface ReplayActionV1 { turnNumber: number; playerIndex: number; input: TurnInput }

/** Parse a record; a version-1 record (the human action stream) is upgraded to the input-log shape. */
export function parseReplay(text: string): ReplayRecord | null {
  try {
    const r = JSON.parse(text) as Record<string, unknown>;
    if (!r || !r.options || !Array.isArray(r.deploySelections)) return null;
    if (r.version === 2 && Array.isArray(r.inputs)) return r as unknown as ReplayRecord;
    if (r.version === 1 && Array.isArray(r.actions)) {
      const { actions, ...rest } = r;
      const inputs: ReplayInput[] = (actions as ReplayActionV1[]).map((a, i) => ({ tick: 0, turn: a.turnNumber, actor: -1, seq: i, body: { kind: 'turn', input: a.input }, playerIndex: a.playerIndex }));
      return { ...(rest as Omit<ReplayRecord, 'version' | 'inputs'>), version: 2, inputs };
    }
    return null;
  } catch { return null; }
}
export function saveLastReplay(record: ReplayRecord): boolean {
  try { localStorage.setItem(REPLAY_STORAGE_KEY, serializeReplay(record)); return true; } catch { return false; }
}
export function loadLastReplay(): ReplayRecord | null {
  try { const raw = localStorage.getItem(REPLAY_STORAGE_KEY); return raw ? parseReplay(raw) : null; } catch { return null; }
}
export function replayFileName(record: ReplayRecord): string {
  const d = record.savedAt.slice(0, 19).replace(/[:T]/g, '-');
  return `orbituary-replay-${record.options.seed}-${d}.json`;
}
